import * as R from 'ramda';
import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';


const KEY_ACTION = ActionType.SendReportRecipientPasswordResetEmailRequest;
const ACTIONS    = [
  ActionType.SendReportRecipientPasswordResetEmailRequest,
  ActionType.SendReportRecipientPasswordResetEmailResponse,
  ActionType.SendReportRecipientPasswordResetEmailError,
];

type Payload = number;

type Meta = {
  emailAddress: string;
  languageId: number;
};

/**
 * Dispatch this action to send the user's answer to their security question (which is required in order to reset their
 * password) when attempting to log in from the report recipient portal
 *
 * @param emailAddress - the email address of the user attempting to answer the question
 * @param languageId - the database Id of the language in which the email should be sent
 */
export const sendReportRecipientPasswordResetEmailAction = (
  emailAddress: string,
  languageId: number,
) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.SendReportRecipientForgotPassword,
  headers:  { 'content-type': 'application/json' },
  body:     JSON.stringify({ toAddress: emailAddress, languageId: languageId }),
  types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { emailAddress: emailAddress, languageId: languageId }),
  bailout:  state => !!getLoading(state, KEY_ACTION, `${emailAddress}-${languageId}`),
});

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.emailAddress}-${action.meta.languageId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  return R.dissocPath(['auth', 'passwordReset'], successState);
};

export const sendReportRecipientPasswordResetEmailReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
